let _decoder;
let _truncatedLine;
let _overReadLength;
let _header;
let _currentElement;
let _currentElementCount;

export default {
  init(header) {
    _decoder = new TextDecoder();
    _truncatedLine = "";
    _overReadLength = 0;
    _header = header;
    _currentElement = 0;
    _currentElementCount = 0;
  },

  decodeASCII(dataChunk, buffer) {
    const textChunk = _decoder.decode(dataChunk, {stream: true});

    const lines = textChunk.split(/\r\n|\r|\n/);
    lines[0] = _truncatedLine + lines[0];
    const lastLine = lines[lines.length - 1];
    const isLastLineFull = lastLine.endsWith("\r\n") || lastLine.endsWith("\r") || lastLine.endsWith("\n");
    const tailDrop = isLastLineFull ? 0 : 1;
    _truncatedLine = isLastLineFull ? "" : lastLine;

    for (let i = 0; i < lines.length - tailDrop; i++) {
      let line = lines[i];
      line = line.trim();
      if (line === "") {
        continue;
      }

      if (_currentElementCount >= _header.elements[_currentElement].count) {
        _currentElement++;
        _currentElementCount = 0;
      }

      const element = _parseASCIIElement(_header.elements[_currentElement].properties, line);

      _handleElement(buffer, _header.elements[_currentElement].name, element);

      _currentElementCount++;
    }
  },

  decodeBinary(dataChunk, dataSize, buffer) {
    const little_endian = ( _header.format === "binary_little_endian" );

    let loc = _overReadLength;
    while (loc < dataSize) {
      if (_currentElementCount >= _header.elements[_currentElement].count) {
        _currentElement++;
        _currentElementCount = 0;
      }

      const elemAndSize = _binaryReadElement( dataChunk, loc, _header.elements[ _currentElement ].properties, little_endian );
      const element = elemAndSize[ 0 ];
      loc += elemAndSize[ 1 ];
      _handleElement(buffer, _header.elements[_currentElement].name, element);

      _currentElementCount++;
    }

    _overReadLength = loc - dataSize;
  },

  arrayConstructorByType(type) {
    switch ( type ) {
      // Note: currently webstudio doesn't support signed integers as indices
      case "int8":		case "char":  case "uint8":		case "uchar":	 return Uint8Array;
      case "int16":		case "short": case "uint16":	case "ushort": return Uint16Array;
      case "int32":		case "int":	  case "uint32":	case "uint":	 return Uint32Array;
      case "float32": case "float":	 return Float32Array;
      case "float64": case "double": return Float64Array;
    }
  },
}

export class Buffer {
  constructor() {
    this.VERTICES = 0;
    this.INDICES = 1;
    this.NORMALS = 2;
    this.UVS = 3;

    this.elementsByFace = 0;
  }

  push(bufferType, value) {
    throw new Error("Buffer is an abstract class. Reimplement push function." + bufferType + value);
  }

  updateElementsByFace(newCount) {
    this.elementsByFace = Math.max(this.elementsByFace, newCount);
  }

  checkOrInitIndices() {}
}

function _parseASCIIElement( properties, line ) {

  const values = line.split( /\s+/ );

  const element = {};

  for ( let i = 0; i < properties.length; i ++ ) {

    if ( properties[ i ].type === "list" ) {

      const list = [];
      const n = _parseASCIINumber( values.shift(), properties[ i ].countType );

      for ( let j = 0; j < n; j ++ ) {

        list.push( _parseASCIINumber( values.shift(), properties[ i ].itemType ) );

      }

      element[ properties[ i ].name ] = list;

    } else {

      element[ properties[ i ].name ] = _parseASCIINumber( values.shift(), properties[ i ].type );

    }

  }

  return element;

}

function _parseASCIINumber( n, type ) {

  switch ( type ) {

    case "char": case "uchar": case "short": case "ushort": case "int": case "uint":
    case "int8": case "uint8": case "int16": case "uint16": case "int32": case "uint32":

      return parseInt( n );

    case "float": case "double": case "float32": case "float64":

      return parseFloat( n );

  }

}

function _binaryReadElement( dataview, at, properties, little_endian ) {

  const element = {};
  let result, read = 0;

  for ( let i = 0; i < properties.length; i ++ ) {

    if ( properties[ i ].type === "list" ) {

      const list = [];

      result = _binaryRead( dataview, at + read, properties[ i ].countType, little_endian );
      const n = result[ 0 ];
      read += result[ 1 ];

      for ( let j = 0; j < n; j ++ ) {

        result = _binaryRead( dataview, at + read, properties[ i ].itemType, little_endian );
        list.push( result[ 0 ] );
        read += result[ 1 ];

      }

      element[ properties[ i ].name ] = list;

    } else {

      result = _binaryRead( dataview, at + read, properties[ i ].type, little_endian );
      element[ properties[ i ].name ] = result[ 0 ];
      read += result[ 1 ];

    }

  }

  return [ element, read ];

}

function _binaryRead( dataview, at, type, little_endian ) {

  switch ( type ) {

      // corespondences for non-specific length types here match rply:
    case "int8":		case "char":	 return [ dataview.getInt8( at ), 1 ];
    case "uint8":		case "uchar":	 return [ dataview.getUint8( at ), 1 ];
    case "int16":		case "short":	 return [ dataview.getInt16( at, little_endian ), 2 ];
    case "uint16":	case "ushort": return [ dataview.getUint16( at, little_endian ), 2 ];
    case "int32":		case "int":		 return [ dataview.getInt32( at, little_endian ), 4 ];
    case "uint32":	case "uint":	 return [ dataview.getUint32( at, little_endian ), 4 ];
    case "float32": case "float":	 return [ dataview.getFloat32( at, little_endian ), 4 ];
    case "float64": case "double": return [ dataview.getFloat64( at, little_endian ), 8 ];

  }

}

function _handleElement( buffer, elementName, element ) {

  function findAttrName( names ) {

    for ( let i = 0, l = names.length; i < l; i ++ ) {

      const name = names[ i ];

      if ( name in element ) return name;

    }

    return null;

  }

  const attrX = findAttrName( [ "x", "px", "posx" ] ) || "x";
  const attrY = findAttrName( [ "y", "py", "posy" ] ) || "y";
  const attrZ = findAttrName( [ "z", "pz", "posz" ] ) || "z";
  const attrNX = findAttrName( [ "nx", "normalx" ] );
  const attrNY = findAttrName( [ "ny", "normaly" ] );
  const attrNZ = findAttrName( [ "nz", "normalz" ] );
  const attrS = findAttrName( [ "s", "u", "texture_u", "tx" ] );
  const attrT = findAttrName( [ "t", "v", "texture_v", "ty" ] );

  if ( elementName === "vertex" ) {
    buffer.push(buffer.VERTICES, element[ attrX ]);
    buffer.push(buffer.VERTICES, element[ attrY ]);
    buffer.push(buffer.VERTICES, element[ attrZ ]);

    if ( attrNX !== null && attrNY !== null && attrNZ !== null ) {
      buffer.push(buffer.NORMALS, element[ attrNX ]);
      buffer.push(buffer.NORMALS, element[ attrNY ]);
      buffer.push(buffer.NORMALS, element[ attrNZ ]);
    }

    if ( attrS !== null && attrT !== null ) {
      buffer.push(buffer.UVS, element[ attrS ]);
      buffer.push(buffer.UVS, element[ attrT ]);
    }

  } else if ( elementName === "face" ) {

    const vertex_indices = element.vertex_indices || element.vertex_index; // issue #9338
    if ( vertex_indices.length === 3 ) {
      buffer.updateElementsByFace(3);
      buffer.checkOrInitIndices();
      buffer.push(buffer.INDICES, vertex_indices[0]);
      buffer.push(buffer.INDICES, vertex_indices[1]);
      buffer.push(buffer.INDICES, vertex_indices[2]);
    } else if ( vertex_indices.length === 4 ) {
      buffer.updateElementsByFace(6);
      buffer.checkOrInitIndices();
      buffer.push(buffer.INDICES, vertex_indices[0]);
      buffer.push(buffer.INDICES, vertex_indices[1]);
      buffer.push(buffer.INDICES, vertex_indices[3]);
      buffer.push(buffer.INDICES, vertex_indices[1]);
      buffer.push(buffer.INDICES, vertex_indices[2]);
      buffer.push(buffer.INDICES, vertex_indices[3]);
    }

  }

}

